import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  AppBar,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Slide,
  Toolbar,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core'
import { ArrowBack, Block, Check, Close, CropPortrait } from '@material-ui/icons'
import { useLiterals } from '~/components/Literals'
import { getGroupName, getAttibuteName, isAttributeEnabled } from './utils/gmb-attribute'

const useStyles = makeStyles((theme) => ({
  listRoot: { width: '100%' },
  listPadding: { paddingBottom: 32 },
  notFoundContainer: {
    width: '100%',
    minHeight: 300,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  textDisabled: {
    color: theme.palette.text.disabled
  },
  desktopDialogContent: {
    padding: '0 16px',
    ...theme.gofind.scrollBar
  }
}))

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

function Item({ attribute, language }) {
  const isEnum = attribute.valueType === 'REPEATED_ENUM'
  const theme = useTheme()

  if (isEnum) {
    const values =
      attribute.repeatedEnumValue.setValues || attribute.repeatedEnumValue.unsetValues || []
    return values.map((value) => (
      <ListItem key={`${attribute.name}_${value.value}`}>
        <ListItemIcon>
          {isAttributeEnabled(attribute) ? <Check /> : <Block color="disabled" />}
        </ListItemIcon>
        <ListItemText
          disableTypography
          style={{
            color: isAttributeEnabled(attribute)
              ? theme.palette.text.primary
              : theme.palette.text.disabled
          }}
        >{`${getAttibuteName(attribute, 'displayName', language)} (${getAttibuteName(
          value,
          'displayName',
          language
        )})`}</ListItemText>
      </ListItem>
    ))
  }

  return (
    <ListItem key={getAttibuteName(attribute, 'displayName', language)}>
      <ListItemIcon>
        {isAttributeEnabled(attribute) ? <Check /> : <Block color="disabled" />}
      </ListItemIcon>
      <ListItemText
        disableTypography
        style={{
          color: isAttributeEnabled(attribute)
            ? theme.palette.text.primary
            : theme.palette.text.disabled
        }}
      >
        {getAttibuteName(attribute, 'displayName', language)}
      </ListItemText>
    </ListItem>
  )
}

function GmbAttributesDetailsDialogMobile({ open, handleClose, attributes }) {
  const classes = useStyles()
  const config = useSelector((state) => state.config)
  const { getLiteral } = useLiterals()

  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar color="transparent" elevation={0} position="relative">
        <Toolbar>
          <IconButton
            size="small"
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <ArrowBack />
          </IconButton>
          <Typography style={{ color: '#322B2D', fontWeight: `bold`, margin: ' 0 auto' }}>
            {getLiteral('2809')}
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent style={{ padding: '0 16px' }}>
        {attributes.map((group) => (
          <List
            key={group[0]['name']}
            className={classes.listRoot}
            classes={{ padding: classes.listPadding }}
            subheader={
              <ListSubheader
                style={{ fontWeight: 'bold', color: theme.palette.text.primary }}
                disableSticky
              >
                {getGroupName(group[0], 'groupDisplayName', config.language)}
              </ListSubheader>
            }
          >
            {group.map((attribute, indice) => (
              <Item attribute={attribute} language={config.language} key={indice} />
            ))}
          </List>
        ))}
      </DialogContent>
    </Dialog>
  )
}

function GmbAttributesDetailsDialogDesktop({ open, handleClose, attributes }) {
  const classes = useStyles()
  const config = useSelector((state) => state.config)
  const { getLiteral } = useLiterals()

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle
        onClose={handleClose}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 8px 8px 30px'
        }}
        disableTypography
      >
        <Typography style={{ fontWeight: 400 }}>{getLiteral('1513')}</Typography>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent className={classes.desktopDialogContent}>
        {attributes.map((group) => (
          <List
            key={group[0]['name']}
            className={classes.listRoot}
            classes={{ padding: classes.listPadding }}
            subheader={
              <ListSubheader
                style={{ fontWeight: 'bold', color: theme.palette.text.primary }}
                disableSticky
              >
                {getGroupName(group[0], 'groupDisplayName', config.language)}
              </ListSubheader>
            }
          >
            {group.map((attribute, indice) => (
              <Item attribute={attribute} language={config.language} key={indice} />
            ))}
          </List>
        ))}
      </DialogContent>
    </Dialog>
  )
}

function GmbAttributesDetailsDialog({ open, setOpen, attributes, isMobile }) {
  const handleClose = () => setOpen(false)

  const groupedAttributes = useMemo(() => {
    const group = attributes.reduce((acc, current) => {
      // Apenas os attributos de acessibilidade negativo são exibidas.
      if (current.groupDisplayName !== 'Acessibilidade' && current.valueType === 'BOOL') {
        if (!current.values[0].value) {
          return acc
        }
      }

      if (acc[current.groupDisplayName]) {
        acc[current.groupDisplayName].push(current)
      } else {
        acc[current.groupDisplayName] = [current]
      }
      return acc
    }, {})

    return Object.values(group)
  }, [attributes])

  return isMobile ? (
    <GmbAttributesDetailsDialogMobile
      open={open}
      handleClose={handleClose}
      attributes={groupedAttributes}
    />
  ) : (
    <GmbAttributesDetailsDialogDesktop
      open={open}
      handleClose={handleClose}
      attributes={groupedAttributes}
    />
  )
}

export default GmbAttributesDetailsDialog
